<template>
  <div ref="refCanvasContainer" class="relative w-full h-full bg-">
    <loader v-if="!collaborationReady" />
    <canvas id="c" />

    <div
      v-show="!connected" class="absolute top-0 right-0 left-0 bottom-0 bg-black bg-opacity-50 text-white flex justify-center items-center z-[2000] text-lg"
      v-text="t('general.connecting')"
    />
    <div class="absolute top-0 bottom-0 left-0 right-0 flex flex-col pointer-events-none">
      <div class="flex h-20 p-3">
        <titlebar
          v-show="!isFullScreen" :whiteboard="wb" :name="whiteboardDetails?.Name" :permission="userPermission" class="flex-1 flex-grow-0 flex-shrink pointer-events-auto"
          @action-click="onTitleBarActionClick" @settings-changed="settingsChanged"
        />
        <div class="flex-grow" />
        <users-bar
          v-show="!isFullScreen" :users="connectedUsers" :whiteboard-id="whiteboardId" :whiteboard="wb" :permission="userPermission" :can-invite="canInvite"
          class="text-right pointer-events-auto"
        />
      </div>
      <div v-if="wb" class="flex-1">
        <objects-toolbar
          ref="objectsToolbarRef" :hide="isFullScreen" :left="isSidebarOpen ? '280px' : '20px'" :whiteboard="wb" :collaboration-ready="collaborationReady" :permission="userPermission"
          :include-details="settings.includeDetails" @object-added="onObjectAdded" @btn-click="onToolbarBtnClick" @handle-images-upload="handleImagesDrop"
        />
      </div>
      <div class="flex h-20 p-3">
        <div class="flex-grow" />
        <div class="flex h-8 m-auto bg-white rounded-lg cursor-pointer pointer-events-auto shadow-toolbar" @wheel.ctrl.prevent @mousemove.prevent>
          <font-awesome-icon icon="fa-light fa-minus" class="w-5 h-5 mx-1 my-auto" @click="zoomOut" />
          <div class="m-auto text-md" @dblclick="resetZoom">
            {{ zoom }}%
          </div>
          <font-awesome-icon icon="fa-light fa-plus" class="w-5 h-5 mx-1 my-auto" @click="zoomIn" />
        </div>
      </div>
    </div>

    <sidebar v-show="!isFullScreen" v-model="isSidebarOpen" :whiteboard="wb" @toggle-fullscreen="toggleFullScreen" />
    <comments-list v-show="!isFullScreen" v-model="showCommentsPanel" :whiteboard="wb" />
    <whiteboard-history v-show="!isFullScreen" v-model="showHistoryPanel" :whiteboard-details="whiteboardDetails" :whiteboard="wb" :permission="userPermission" />
    <summary-dialog
      ref="summaryDialogRef" :summary-report-config="userStore.activeCatalog?.Config.WhiteboardSummaryReport"
      :summary-chart-settings="userStore.settingsContainer?.whiteboard?.whiteboardSummaryCharts" :show-include-in-active="true"
    />
    <object-properties
      v-if="wb" v-show="!isFullScreen" ref="objectPropertiesRef" :whiteboard-id="whiteboardId" :whiteboard="wb" :permission="userPermission" @design-option-added="onDesignOptionsAdded"
      @open-text-editing-dialog="onOpenTextEditingDialog" @copy-model="onCopyModel" @edit-articles="onEditArticles" @copy-articles="onCopyArticles" @save-images="onSaveImages"
      @edit-favorites="showFav" @handle-images-drop="handleImagesDrop" @delete-image="onDeleteImage" @article-updated="onArticlesUpdated"
    />

    <!-- Sticky text editing dialog -->
    <tx-dialog v-model="textEditingDialogVisible" :title="t('general.edit')" show-ok-cancel @ok="setObjectText">
      <tx-input ref="objectTextRef" v-model="objectText" type="textarea" />
    </tx-dialog>

    <!-- Article Details -->
    <tx-drawer
      v-model="articleDetailsOpen" :width="enableNewStyleDetails ? '95%' : '1024px'" right
      :show-overlay="false" @closed="closeArticleDetails(), otherWindowOpened(false)" @opened="otherWindowOpened(true)"
    >
      <model-details
        v-if="enableNewStyleDetails && clickedArticle" ref="modelDetailsRef" :article="clickedArticle" @updated="onArticleUpdated" @change="onArticleClick"
        @close="closeArticleDetails" @on-edit-fav="onArticleDetailsEditFav" @article-assets-updated="onArticleAssetsUpdated" @swap-article="swapArticle"
        @on-updated-articles="onUpdatedArticles" @design-option-deleted="onDesignOptionDeleted"
      />
      <article-details
        v-else-if="clickedArticle" ref="articleDetailsRef" :article="clickedArticle" :design-option-added="addedDesignOptionsId" @updated="onArticleUpdated" @change="onArticleClick"
        @on-edit-fav="onArticleDetailsEditFav" @article-assets-updated="onArticleAssetsUpdated" @swap-article="swapArticle"
        @on-updated-articles="onUpdatedArticles" @design-option-deleted="onDesignOptionDeleted"
      />
    </tx-drawer>
    <!-- EDIT ARTICLES -->
    <tx-drawer v-model="updateArticlesDrawerVisible" width="700px" right :show-overlay="false" @closed="closeUpdateArticles(), otherWindowOpened(false)" @opened="otherWindowOpened(true)">
      <update-article
        v-if="updateActionType === ActionBarActionType.updateArticles" ref="updateArticleRef"
        :articles="selectedArticlesForEdit" :is-model="false"
        @cancel="closeUpdateArticles" @updated="onArticlesUpdated"
      />
    </tx-drawer>
    <!-- STYLE CREATE DRAWER -->
    <tx-drawer
      v-model="isAddArticleDrawerVisible" width="90%" right :show-close="false" :close-on-esc="false"
      :close-on-overlay-click="false" :show-overlay="false" @opened="otherWindowOpened(true)" @closed="otherWindowOpened(false)"
    >
      <add-articles
        :action-type="styleCreateAction" :context-article="copyStyleContextArticle"
        :visible="isAddArticleDrawerVisible" @close="onAddArticleDrawerClosed"
      />
    </tx-drawer>

    <!-- Find & Replace Dialog -->
    <find-and-replace-dialog v-model="findReplaceDialogVisible" :whiteboard="wb" />

    <!-- Generate frame dialog -->
    <generate-dialog ref="refGenerateFrameDialog" v-model="generateFrameDialogVisible" :whiteboard="wb" :is-merch="false" @opened="otherWindowOpened(true)" @closed="otherWindowOpened(false)" />

    <!-- Save Images dialog -->
    <save-image-dialog ref="saveImageDlg" />

    <favorites-editor-dialog ref="favoritesDlg" />

    <!-- Upload image progress-bar -->
    <tx-dialog
      v-model="uploadImageProgressDialogVisible" :title="t('whiteboard.uploadImage.title')" show-ok :ok-state="uploadImageProgress < 100 ? 'disabled' : 'enabled'"
      confirm-text="general.close" @ok="closeUploadImageDialog"
    >
      <tx-progress v-model="uploadImageProgress" :title="uploadImagesProgressTitle" />
      <div v-if="uploadImageProgress === 100" class="p-2 text-m italic text-red-500 overflow-auto">
        {{ uploadImageErrorMessage }}
      </div>
    </tx-dialog>
  </div>
</template>

<script setup lang="ts">
import { fabric } from 'fabric'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, shallowRef, watch } from 'vue'
import ArticleDetails from '../articleDetails/ArticleDetails.vue'
import ObjectsToolbar from './components/ObjectsToolbar.vue'
import Sidebar from './components/Sidebar.vue'
import CommentsList from './components/CommentsList.vue'
import WhiteboardHistory from './components/whiteboardHistory'
import ObjectProperties from './components/ObjectProperties.vue'
import Titlebar from './components/Titlebar.vue'
import UsersBar from './components/UsersBar.vue'
import WbModelImage from './services/modelImage'
import WbArticleImage from './services/articleImage'
import WbArticleDetails from './services/articleDetails'
import WbModelDetails from './services/modelDetails'
import usePresence from './composables/presence'
import useCollaboration from './composables/collaboration'
import useWindowEvents from './composables/windowEvents'
import WbFrame from './services/frame'
import WbGroup from './services/group'
import WbDesignOption from './services/designOption'
import type { ISettingItemWhiteboard } from './components/Settings.vue'
import { useConfirmDialog } from '@/shared/composables/confirmDialog'
import SaveImageDialog from '@/modules/browse/components/SaveImageDialog.vue'
import type { ICanvasObjectsLoadedEvent } from '@/modules/whiteboard/services/whiteboard'
import Whiteboard from '@/modules/whiteboard/services/whiteboard'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxDrawer from '@/shared/components/TxDrawer.vue'
import FindAndReplaceDialog from '@/modules/whiteboard/components/FindAndReplaceDialog.vue'
import GenerateDialog from '@/shared/components/GenerateDialog.vue'
import ModelDetails from '@/modules/articleDetails/ModelDetails.vue'
import AddArticles from '@/modules/browse/components/addArticles'
import type MyArticle from '@/models/myArticle'
import { getWhiteboardDetails } from '@/api/t1/whiteboard'
import type { WhiteboardDetailsModel } from '@/api/t1/model/whiteboardModel'
import { privileges, whiteboardConstants } from '@/models/constants'
import useArticleDetails from '@/shared/composables/articleDetails'
import { useUserStore } from '@/store/userData'
import { useNotificationStore } from '@/store/notification'
import appConfig from '@/services/appConfig'
import Loader from '@/shared/components/Loader.vue'
import UpdateArticle from '@/modules/articleDetails/components/UpdateArticle.vue'
import { getAccessibleUserGroupDetails } from '@/api/t1/user'
import FavoritesEditorDialog from '@/shared/components/favorites/FavoritesEditorDialog.vue'
import TxProgress from '@/shared/components/TxProgress.vue'
import SummaryDialog from '@/shared/components/summaryDialog'
import type DuneAsset from '@/models/duneAsset'
import net from '@/services/net'
import Article from '@/models/article'
import { useWhiteboardStore } from '@/store/whiteboard'
import { changeAssetStatus } from '@/api/dune/asset'
import useEventsBus from '@/shared/composables/eventBus'
import { getArticleAssets } from '@/api/t1/article'
import utils from '@/services/utils'

enum ActionBarActionType {
  updateArticles = 'updateArticles',
}

const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()
const router = useRouter()
const notificationStore = useNotificationStore()
const whiteboardStore = useWhiteboardStore()
const confirmDialog = useConfirmDialog()
const { emitter } = useEventsBus()

const { articleDetailsOpen, modelDetailsRef, articleDetailsRef, clickedArticle, onArticleUpdated, onArticleClick, openArticleDetails, closeArticleDetails } = useArticleDetails('WhiteboardDetails')

// VARIABLES
const refCanvasContainer = ref<HTMLElement>()
const objectsToolbarRef = ref<InstanceType<typeof ObjectsToolbar>>()
const isSidebarOpen = ref(false)
const showCommentsPanel = ref(false)
const showHistoryPanel = ref(false)
const summaryDialogRef = ref<InstanceType<typeof SummaryDialog>>()
const updateArticleRef = ref<InstanceType<typeof UpdateArticle>>()
const selectedEditingObj = ref<IWbObject>()
const objectText = ref('')
const objectTextRef = ref<InstanceType<typeof TxInput> | null>()
const zoom = ref(100)
const wb = shallowRef<Whiteboard>()
const whiteboardDetails = shallowRef<WhiteboardDetailsModel>()
const refGenerateFrameDialog = ref<InstanceType<typeof GenerateDialog>>()
const objectPropertiesRef = ref<InstanceType<typeof ObjectProperties>>()
const isAddArticleDrawerVisible = ref(false)
const updateActionType = ref<ActionBarActionType>()
const updateArticlesDrawerVisible = ref(false)
const settings = reactive<ISettingItemWhiteboard>({
  showComments: false,
  liveUpdates: true,
  includeDetails: true,
  showArticleDesignOptions: true,
  showModelDesignOptions: true,
  showFavorites: false,
  zoomSensitivity: 90,
  newArticleIndicator: true,
})
let eventSource: EventSource | null = null
let explicitlyClosedES = false

let selectedArticlesForEdit: MyArticle[] = []
let styleCreateAction: StyleCreateActionType | undefined
let copyStyleContextArticle: MyArticle | undefined
let copyStyleSelectedObjectDetails: { left: number, top: number, type: string } | undefined
const userPermission = ref(whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType)
const saveImageDlg = ref<InstanceType<typeof SaveImageDialog>>()
const favoritesDlg = ref<InstanceType<typeof FavoritesEditorDialog>>()
const swapArticleId = ref(-1)
const whiteboardId = computed(() => Number(route.params.whiteboardId))
const addedDesignOptionsId = ref('')

const { collaborationReady, connected, dbConn, dbDoc, userDoc } = useCollaboration(whiteboardId.value, wb)
const usersPresence = usePresence(whiteboardId.value, wb, dbConn)
const whiteboardImageMaxSize = userStore.activeCatalog?.Config.WhiteboardImageMaxSize || 500
const { isOtherWindowOpen, textEditingDialogVisible, findReplaceDialogVisible, generateFrameDialogVisible, isFullScreen, isArticleOrModelDetailsVisible, isDisableCopyPasteUndoRedo, handleImagesDrop, closeUploadImageDialog, uploadImageProgressDialogVisible, uploadImagesProgressTitle, uploadImageProgress, uploadImageErrorMessage } = useWindowEvents(wb, whiteboardId, whiteboardImageMaxSize)

const connectedUsers = computed(() => Array.from(usersPresence.users.value.values()))
const enableNewStyleDetails = computed(() => userStore.activeCatalog?.Config.EnableNewStyleDetails || false)
// only owner and admin user can invite users
const canInvite = computed(() => whiteboardDetails.value?.CreatedBy === userStore.userProfile.Id || userStore.userProfile.AccountDetails.AccountTypeId === 1)

const allowArticleCacheUpdatedEventStream = computed(() => userStore.userProfile.isValidPrivilege(privileges.article.cacheUpdatedEventStream))

function onResizeContent({ width, height }) {
  wb.value?.setSize(width, height)
}

function zoomIn() {
  if (wb.value) {
    const oldZoom = wb.value.canvas.getZoom()
    const zoom = oldZoom * 1.2
    if (zoom <= 5) {
      wb.value.zoom(zoom, { x: wb.value.canvas.getWidth() / 2, y: wb.value.canvas.getHeight() / 2 }, true)
    }
  }
}

function zoomOut() {
  if (wb.value) {
    const oldZoom = wb.value.canvas.getZoom()
    const zoom = oldZoom / 1.2
    if (zoom >= 0.02) {
      wb.value.zoom(zoom, { x: wb.value.canvas.getWidth() / 2, y: wb.value.canvas.getHeight() / 2 }, true)
    }
  }
}

function resetZoom() {
  if (wb.value) {
    wb.value.zoom(1, { x: wb.value.canvas.getWidth() / 2, y: wb.value.canvas.getHeight() / 2 }, true)
  }
}

function onTitleBarActionClick(action: 'comment' | 'history' | 'summary') {
  if (action === 'comment') {
    showCommentsPanel.value = !showCommentsPanel.value
    showHistoryPanel.value = false
  }
  else if (action === 'history') {
    showHistoryPanel.value = !showHistoryPanel.value
    showCommentsPanel.value = false
  }
  else if (action === 'summary') {
    summaryDialogRef.value?.open(getWhiteboardArticles())
  }
}

function getWhiteboardArticles() {
  const articles: MyArticle[] = []
  if (wb.value) {
    const articleIds = new Set<string>()
    const allObjects = wb.value.canvas.getObjects() as any[]
    for (const obj of allObjects) {
      if (obj.article) {
        if (articleIds.has(obj.article.CatalogArticleId)) { continue }

        if (obj.type === whiteboardConstants.objectTypes.articleImage) {
          articles.push(obj.article)
          articleIds.add(obj.article.CatalogArticleId)
        }
        else if (obj.type === whiteboardConstants.objectTypes.articleDetails) {
          articles.push(obj.article)
          articleIds.add(obj.article.CatalogArticleId)
        }
      }
    }
  }
  return articles
}

function settingsChanged(form: ISettingItemWhiteboard) {
  if (showCommentsPanel.value && !form.showComments) {
    showCommentsPanel.value = false
  }
  if (settings.liveUpdates !== form.liveUpdates) {
    settings.liveUpdates = form.liveUpdates
  }
  if (form.includeDetails !== settings.includeDetails) {
    settings.includeDetails = form.includeDetails
  }
  const allObjects = wb.value?.canvas.getObjects() as Array<IWbObject>
  if (allObjects && allObjects.length) {
    const discussionObjects = allObjects.filter(o => o.type === whiteboardConstants.objectTypes.discussion)
    if (discussionObjects.length) {
      discussionObjects.forEach((object) => {
        object.set('visible', form.showComments)
        const div = document.getElementById('manageDiscussion')
        if (div) {
          div.style.opacity = object.visible ? '1' : '0' // Smooth fade effect
          div.style.pointerEvents = object.visible ? 'auto' : 'none' // Disable inter
        }
      })
    }
    if (!utils.isDefined(settings.showArticleDesignOptions) || (utils.isDefined(form.showArticleDesignOptions) && Boolean(form.showArticleDesignOptions) !== Boolean(settings.showArticleDesignOptions))) {
      const articleImageObjects = allObjects.filter(o => o.type === whiteboardConstants.objectTypes.articleImage) as WbArticleImage[]
      if (articleImageObjects.length) {
        articleImageObjects.forEach((object) => {
          if (object.showDesignOptions !== Boolean(form.showArticleDesignOptions)) {
            object.setProp('showDesignOptions', { showDesignOptions: Boolean(form.showArticleDesignOptions) }, true)
          }
        })
      }
      settings.showArticleDesignOptions = Boolean(form.showArticleDesignOptions)
    }
    if (!utils.isDefined(settings.showModelDesignOptions) || (utils.isDefined(form.showModelDesignOptions) && Boolean(form.showModelDesignOptions) !== Boolean(settings.showModelDesignOptions))) {
      const articleImageObjects = allObjects.filter(o => o.type === whiteboardConstants.objectTypes.modelDetails || o.type === whiteboardConstants.objectTypes.modelImage) as WbModelDetails[] | WbModelImage[]
      if (articleImageObjects.length) {
        articleImageObjects.forEach((object) => {
          if (object.showDesignOptions !== Boolean(form.showModelDesignOptions)) {
            object.setProp('showDesignOptions', { showDesignOptions: Boolean(form.showModelDesignOptions) }, true)
          }
        })
      }
      settings.showModelDesignOptions = Boolean(form.showModelDesignOptions)
    }
    wb.value?.canvas.requestRenderAll()
  }
}
function handleDoubleClick() {
  let showArticleDetails = false
  let articleId = -1
  let isRequest = false
  let catalogCode = -1
  let article: MyArticle | undefined
  swapArticleId.value = -1
  const obj = wb.value?.canvas.getActiveObject() as IWbObject
  if (obj && obj.type === whiteboardConstants.objectTypes.sticky) {
    const prop = obj.getProp('text')
    objectText.value = prop.text
    selectedEditingObj.value = obj
    textEditingDialogVisible.value = userPermission.value === whiteboardConstants.userPermissions.write
    nextTick(() => {
      objectTextRef.value?.focus()
    })
  }

  if (obj && (obj.type === whiteboardConstants.objectTypes.articleImage || obj.type === whiteboardConstants.objectTypes.modelImage || obj.type === whiteboardConstants.objectTypes.designOption)) {
    showArticleDetails = true
    if (obj instanceof WbArticleImage) {
      catalogCode = obj.catalogCode
      articleId = obj.articleId
      isRequest = obj.isRequest
      article = obj.article
    }
    else if (obj instanceof WbModelImage) {
      catalogCode = obj.catalogCode
      articleId = obj.articleId
      article = obj.article
    }
    else if (obj instanceof WbDesignOption) {
      catalogCode = userStore.activeCatalog!.CatalogCode
      articleId = obj.articleId
      article = obj.article
    }
  }
  else if (obj && (obj.type === whiteboardConstants.objectTypes.group)) {
    if (obj instanceof WbGroup) {
      const articleImages = obj._objects.filter(o => o.type === whiteboardConstants.objectTypes.articleImage)
      const modelImages = obj._objects.filter(o => o.type === whiteboardConstants.objectTypes.modelImage)
      // if there is one article image, show article detail
      if (articleImages.length === 1 && modelImages.length === 0) {
        if (articleImages[0] instanceof WbArticleImage) {
          articleId = articleImages[0].articleId
          article = articleImages[0].article
        }
        showArticleDetails = true
      }
      else if (modelImages.length === 1 && articleImages.length === 0) {
        if (modelImages[0] instanceof WbModelImage) {
          articleId = modelImages[0].articleId
          article = modelImages[0].article
        }
        showArticleDetails = true
      }
    }
  }

  if (userStore.activeCatalog && showArticleDetails) {
    if (isRequest && articleId !== -1) {
      appConfig.DB!.getRequestArticleById(userStore.activeCatalog!, articleId, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
        .then((article) => {
          if (article) {
            onArticleClick(article)
          }
        })
    }
    else {
      if (obj.type === whiteboardConstants.objectTypes.articleImage && obj instanceof WbArticleImage && !obj.dynamicGrid && catalogCode === userStore.activeCatalog!.CatalogCode) {
        swapArticleId.value = articleId
      }
      if (article) {
        onArticleClick(article, false, swapArticleId.value !== -1)
      }
      else if (articleId !== -1) {
        const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
        appConfig.DB!.getMyArticlesByIds(catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, [articleId], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
          .then(([article]) => onArticleClick(article, false, swapArticleId.value !== -1))
      }
    }
  }
}

function setObjectText() {
  if (selectedEditingObj.value) {
    selectedEditingObj.value?.setProp('text', { text: objectText.value })
  }
  textEditingDialogVisible.value = false
}

function onToolbarBtnClick(btn: IToolbarButton<MerchToolbarAction>) {
  if (btn.key === 'generate') {
    refGenerateFrameDialog.value?.showDialog()
  }
}

function onObjectAdded(obj: IWbObject) {
  if (obj && wb.value) {
    if (obj.type === whiteboardConstants.objectTypes.sticky) {
      wb.value.canvas.setActiveObject(obj)
      handleDoubleClick()
    }
    else if (obj.type === whiteboardConstants.objectTypes.discussion) {
      wb.value.canvas.setActiveObject(obj)
    }
    else if (obj.type === whiteboardConstants.objectTypes.frame) {
      wb.value.sendObjectsToBack([obj])
    }
    else if (obj.type === whiteboardConstants.objectTypes.articleImage || obj.type === whiteboardConstants.objectTypes.articleDetails) {
      if (summaryDialogRef.value && summaryDialogRef.value.visible) {
        summaryDialogRef.value.updateArticles(getWhiteboardArticles())
      }
    }
  }
}

function onOpenTextEditingDialog(obj: IWbObject) {
  if (obj) {
    const prop = obj.getProp('text')
    objectText.value = prop.text
    selectedEditingObj.value = obj
    textEditingDialogVisible.value = true
    nextTick(() => {
      objectTextRef.value?.focus()
    })
  }
}

function onCopyModel(articleOrModelImage: WbArticleImage | WbModelImage) {
  copyStyleSelectedObjectDetails = {
    top: articleOrModelImage.aCoords?.tr.y || 0,
    left: articleOrModelImage.aCoords?.tr.x || 0,
    type: articleOrModelImage.type,
  }
  if (articleOrModelImage.article) {
    copyStyleContextArticle = articleOrModelImage.article
    styleCreateAction = 'copyModel'
    isAddArticleDrawerVisible.value = true
  }
}
async function closeUpdateArticles() {
  if (updateArticleRef.value && selectedArticlesForEdit.length && updateActionType.value === ActionBarActionType.updateArticles) {
    if (updateArticleRef.value.isFormDirty()) {
      updateArticlesDrawerVisible.value = true
      const dialogResult = await confirmDialog(t('updateArticle.youWantToLeaveThePage'), t('updateArticle.youSaveYourChangesDiscardContinue'), [], undefined, async () => {
        if (appConfig.DB) {
          await appConfig.DB.articleForms.bulkDelete(selectedArticlesForEdit.map(art => [art.CatalogCode, art.Id, userStore.userProfile.Id, 'editArticle']))
        }
      }, 'general.save', undefined, true)
      if (dialogResult !== 'cancel') {
        updateActionType.value = undefined
        updateArticlesDrawerVisible.value = false
      }
    }
  }
  else {
    updateActionType.value = undefined
    updateArticlesDrawerVisible.value = false
  }
}
function onArticlesUpdated(articles: MyArticle[] | Article[]) {
  notificationStore.addNotification({ message: t('updateArticle.updateSuccessfully', articles.length), type: 'Success' })
  updateActionType.value = undefined
  closeUpdateArticles()
  if (appConfig.DB && userStore.activeCatalog && userStore.myAttributes && articles.length) {
    appConfig.DB.getMyArticlesByIds(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, articles.map(x => x.Id), userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
      .then((myArticles) => {
        onUpdatedArticles(myArticles)
      })
  }
}
function showFav(articleImagesObjects: WbArticleImage[]) {
  favoritesDlg.value?.showDialog(articleImagesObjects.filter(obj => obj.article).map(obj => obj.article!))
}
function onEditArticles(articleImagesObjects: WbArticleImage[]) {
  selectedArticlesForEdit = articleImagesObjects.filter(obj => obj.article).map(obj => obj.article!)
  updateActionType.value = ActionBarActionType.updateArticles
  updateArticlesDrawerVisible.value = true
}
function onCopyArticles(articleImage: WbArticleImage) {
  if (userStore.activeCatalog) {
    copyStyleSelectedObjectDetails = {
      top: articleImage.aCoords?.tr.y || 0,
      left: articleImage.aCoords?.tr.x || 0,
      type: articleImage.type,
    }
    const catalogDetails = userStore.activeCatalog
    if (articleImage.article) {
      copyStyleContextArticle = articleImage.article as MyArticle
      styleCreateAction = 'copyArticle'
      isAddArticleDrawerVisible.value = true
    }
    else {
      appConfig.DB!.getMyArticlesByIds(catalogDetails, {}, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, [articleImage.articleId], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
        .then(([article]) => {
          copyStyleContextArticle = article
          styleCreateAction = 'copyArticle'
          isAddArticleDrawerVisible.value = true
        })
    }
  }
}
function onSaveImages(articleImagesObjects: WbArticleImage[], isWhiteboard: boolean = false) {
  saveImageDlg.value?.showDialog(articleImagesObjects.filter(obj => obj.article).map(obj => obj.article!), isWhiteboard)
}
function onDesignOptionsAdded(assetId: string) {
  addedDesignOptionsId.value = assetId
}

function onUpdatedArticles(articles: MyArticle[]) {
  if (!settings.liveUpdates && userStore.activeCatalog) {
    wb.value?.updateCanvasArticles(userStore.activeCatalog, articles)
  }
  // reload SKUs for model details
  if (userStore.activeCatalog && wb.value && articles.length) {
    const modelIds = new Set(articles.map(x => x.ModelId))
    const modelDetailsObjects = wb.value.canvas.getObjects(whiteboardConstants.objectTypes.modelDetails)
    modelDetailsObjects.forEach((obj) => {
      if (obj.type === whiteboardConstants.objectTypes.modelDetails && obj instanceof WbModelDetails
        && obj.article && modelIds.has(obj.article.ModelId)) {
        // reload SKUs for model details
        delete obj.article.currentSKuCount
        delete obj.article[userStore.activeCatalog!.CatalogCode]
        if (userStore.activeCatalog!._LinkedCatalogCodes.length) {
          userStore.activeCatalog!._LinkedCatalogCodes.forEach((cc) => {
            delete obj.article![cc]
          })
        }
        WbModelDetails.getText(obj.article, obj.showLabels, obj.attributes, obj.attributesSeparator, obj.attributePlaceHolder)
          .then((text) => {
            obj.set('text', text)
            obj.dirty = true
            wb.value?.canvas?.fire('object:modified', { target: obj, ignoreHistory: true })
          })
      }
    })
  }
}

function onArticleDetailsEditFav(article: MyArticle) {
  favoritesDlg.value?.showDialog([article])
}

async function onDeleteImage(activeObject: WbArticleImage) {
  const currentAsset = activeObject.assets.find(asset => asset.Id === activeObject.assetKey || asset.Key === activeObject.assetKey)
  if (userStore.activeCatalog && currentAsset && activeObject) {
    const catalogDetails = userStore.linkedCatalogDetails[activeObject.catalogCode] || userStore.activeCatalog
    await confirmDialog(t('general.alert'), t('articleDetails.deleteAsset'), [], async () => {
      await changeAssetStatus(userStore.activeCatalog!.DuneContext, userStore.activeCatalog!.ContextKey, [currentAsset.Id], false).then(async () => {
        notificationStore.addNotification(({ message: t('articleDetails.alerts.imageDeletedSuccessfully'), type: 'Success' }))
        if (activeObject.articleNumber) {
          const articleAssets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, activeObject.articleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
          onArticleAssetsUpdated(activeObject.articleId, articleAssets)
          emitter('reloadDeletedArticleImage', { articleNumber: activeObject.articleNumber })
        }
      }).catch(() => {
        notificationStore.addNotification(({ message: t('articleDetails.alerts.imageDeletedFailed'), type: 'Alert' }))
      })
    })
  }
}

async function onArticleAssetsUpdated(articleId: number, assets: DuneAsset[]) {
  if (!wb.value) { return }

  const wbObjs = wb.value.canvas.getObjects() as any[]
  const sameArticleObjects: WbArticleImage[] = wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage && obj.articleId! && obj.articleId === articleId)
  sameArticleObjects.forEach((obj) => {
    obj.assets = assets
    if (!assets.length) {
      obj.setProp('imageType', { assetKey: undefined }, true)
    }
  })
  if (assets.length) {
    onAssetPropChange(assets[0], sameArticleObjects)
  }
}

function onDesignOptionDeleted(assetId: string, isArticleDesignOption: boolean, article: MyArticle) {
  if (!wb.value) { return }

  const wbObjs = wb.value.canvas.getObjects() as any[]
  let targetObjects: (WbModelDetails | WbModelImage | WbArticleImage)[] = []
  if (isArticleDesignOption) {
    targetObjects = wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage && obj.article && obj.article.ArticleNumber === article.ArticleNumber)
  }
  else {
    targetObjects = [
      ...wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.modelImage && obj.article && obj.article.ModelNumber === article.ModelNumber),
      ...wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.modelDetails && obj.article && obj.article.ModelNumber === article.ModelNumber),
    ]
  }

  targetObjects.forEach((obj) => {
    const showDesignOptions = obj.showDesignOptions || false
    obj.setProp('showDesignOptions', { showDesignOptions })
  })
}

function onAssetPropChange(asset: DuneAsset, objects: any[] = []) {
  if (!wb.value) { return }
  objects?.forEach((o) => {
    (o as any).setProp('imageType', { assetKey: asset.Key }, true)
  })
}

async function onAddArticleDrawerClosed(articles: Array<MyArticle> | null | undefined) {
  if (articles == null) { // close without creating articles
    onAddArticleClosed()
  }
  else if (articles.length) { // click next on preview
    try {
      await addArticlesToWhiteboard(articles)
    }
    catch (error) {
      console.warn(error)
    }
    finally {
      onAddArticleClosed()
    }
  }
}

async function addArticlesToWhiteboard(articles: Array<MyArticle>) {
  let top = copyStyleSelectedObjectDetails!.top
  let left = copyStyleSelectedObjectDetails!.left
  const addedObjects: Array<WbArticleImage | WbModelImage | WbArticleDetails | WbModelDetails> = [] as Array<WbArticleImage | WbModelImage | WbArticleDetails | WbModelDetails>
  // in case of model only 1 model object should be added to canvas
  const articlesToAddToCanvas = copyStyleSelectedObjectDetails!.type === whiteboardConstants.objectTypes.articleImage ? articles.length : 1
  for (let i = 0; i < articlesToAddToCanvas; i++) {
    const article = articles[i]
    let newArticleObject
    let newArticleDetailsObject
    try {
      if (copyStyleSelectedObjectDetails!.type === whiteboardConstants.objectTypes.articleImage) {
        newArticleObject = await WbArticleImage.loadArticleImage(article, 500, 500, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle })
        if (newArticleObject) {
          // add article details
          // TODO: need to check what is the need of 52 multiplier and setting height of details explicitly
          newArticleDetailsObject = await WbArticleDetails.loadArticleDetails(article, { left, top: top + newArticleObject.height - (52 * 1.35) })
          // set article details object height otherwise it will take random height and while selection unnecessary white space is selected
          newArticleDetailsObject.setProp('height', 52)
        }
      }
      else if (copyStyleSelectedObjectDetails!.type === whiteboardConstants.objectTypes.modelImage) {
        newArticleObject = await WbModelImage.loadModelImage(article, 500, 500, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, modelId: article.ModelId })
        if (newArticleObject) {
          // add model details
          // TODO: need to check what is the need of 52 multiplier and setting height of details explicitly
          newArticleDetailsObject = await WbModelDetails.loadModelDetails(article, { left, top: top + newArticleObject.height - (52 * 1.35) })
          // set article details object height otherwise it will take random height and while selection unnecessary white space is selected
          newArticleDetailsObject.setProp('height', 52)
        }
      }
      if (newArticleObject && newArticleDetailsObject) {
        addedObjects.push(newArticleObject)
        addedObjects.push(newArticleDetailsObject)
        top += 100
        left += 100
      }
    }
    catch (error) {
      console.warn(`Unable to add article: ${article.ArticleNumber} \n`, error)
    }
  }
  wb.value!.canvas.discardActiveObject()
  if (addedObjects.length) {
    wb.value!.addObjects(addedObjects, true)
  }
  const selection = new fabric.ActiveSelection(addedObjects, { canvas: wb.value!.canvas })
  wb.value!.canvas.setActiveObject(selection)
}

function onAddArticleClosed() {
  isAddArticleDrawerVisible.value = false
  copyStyleContextArticle = undefined
  styleCreateAction = undefined
  copyStyleSelectedObjectDetails = undefined
}

async function swapArticle(targetArticle: MyArticle) {
  if (wb.value && swapArticleId.value !== -1) {
    await wb.value.swapArticle(swapArticleId.value, targetArticle)
    clickedArticle.value = null
    closeArticleDetails()
    notificationStore.addNotification({ message: t('merch.messages.articlesSwappedSuccessfully'), type: 'Success' })
  }
}

function toggleFullScreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen()
    isFullScreen.value = false
  }
  else {
    refCanvasContainer.value?.requestFullscreen()
      .then(() => {
        isFullScreen.value = true
        // isSidebarOpen.value = false
      })
      .catch((error) => {
        console.warn(`unable to go full screen, \n ${error}`)
        isFullScreen.value = false
      })
  }
}

function handleFullScreen() {
  isFullScreen.value = !!(document.fullscreenElement)
}

async function gotoRouteSelection() {
  if (route.name && route.name.toString() === 'WhiteboardDetails') {
    whiteboardStore.setCurrentBoardId(Number(whiteboardId.value))
  }
  if (!['WhiteboardDetails', 'WhiteboardDetailsArticleDetails'].includes(route.name!.toString())) { return }
  if (userStore.activeCatalog && route.params && route.params.articleId) {
    await openArticleDetails()
  }
  else if (articleDetailsOpen.value) {
    clickedArticle.value = null
    closeArticleDetails()
  }
}

function onWindowResize() {
  onResizeContent({ width: window.innerWidth - 80, height: window.innerHeight })
}

function onSelected(e: fabric.IEvent<MouseEvent>) {
  if (e.selected) {
    for (const obj of e.selected as Array<IWbObject>) {
      if (obj.selectable && !obj.lock) {
        if (obj instanceof WbFrame) {
          obj.resetObjMoving(false)
        }
      }
    }
  }
}

function updateCanvasPermissions(obj: Whiteboard) {
  if (userPermission.value === whiteboardConstants.userPermissions.write) {
    // setCanvasObjectPermission(obj, true, true)
  }
  else if (userPermission.value === whiteboardConstants.userPermissions.readAndComment) {
    setCanvasObjectPermission(obj, false, true, whiteboardConstants.objectTypes.discussion)
  }
  else if (userPermission.value === whiteboardConstants.userPermissions.read) {
    setCanvasObjectPermission(obj, false, false, whiteboardConstants.objectTypes.discussion)
  }
}

function setCanvasObjectPermission(obj: Whiteboard, selectable: boolean, evented: boolean, targetObjectType?: string) {
  const objects = obj.canvas.getObjects() as fabric.Object[]
  if (objects) {
    objects.forEach((canvasObject) => {
      if (targetObjectType && canvasObject.type === targetObjectType) { // enable comment to clickable
        canvasObject.selectable = true
        canvasObject.evented = true
      }
      else {
        canvasObject.selectable = selectable
        canvasObject.evented = evented
        canvasObject.hasControls = selectable
      }
    })
  }
  obj.canvas.discardActiveObject()
  obj.canvas.requestRenderAll()
}

function otherWindowOpened(isOpen: boolean) {
  isOtherWindowOpen.value = isOpen
}

function highlightObjectOnBoard(board: Whiteboard, objId: string) {
  const wbObjs = board.canvas.getObjects() as IWbObject[]
  if (wbObjs) {
    const objToHighlight = wbObjs.filter(obj => obj.id === objId)[0]
    if (objToHighlight) {
      wb.value!.canvas.setActiveObject(objToHighlight)
      board.moveToView(objToHighlight)
    }
  }
}

function initEventSource() {
  if (userStore.activeCatalog && eventSource == null && allowArticleCacheUpdatedEventStream.value) {
    eventSource = new EventSource(`${net.t1v2.baseURL}/catalogs/${userStore.activeCatalog!.CatalogCode}/articles/eventstream?checkLinkedCatalogs=true&token=${net.t1v2.token}`)
    eventSource.onerror = (e) => {
      console.error('EventSource error', e)
      if (eventSource?.readyState === EventSource.CLOSED && !explicitlyClosedES) {
        eventSource.close()
        eventSource = null
        setTimeout(initEventSource, 1000)
      }
    }
    eventSource.addEventListener('article-updated', onArticleUpdatedEventSource)
  }
}

function closeEventSource() {
  eventSource?.close()
  if (eventSource) {
    eventSource.removeEventListener('article-updated', onArticleUpdatedEventSource)
  }
  eventSource = null
  explicitlyClosedES = true
}

async function onArticleUpdatedEventSource(event: MessageEvent) {
  try {
    if (appConfig.DB && userStore.activeCatalog && userStore.myAttributes && collaborationReady.value) {
      const articleObj = JSON.parse(event.data)
      const catalogDetails = userStore.linkedCatalogDetails[articleObj.CatalogCode] || userStore.activeCatalog
      const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)
      const article = new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, articleObj, catalogDetails.DataSourceTypeId, datasetAttributes)
      const myArticles = await appConfig.DB.buildMyArticles([article], catalogDetails, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(userStore.linkedCatalogDetails), userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
      wb.value?.updateCanvasArticles(catalogDetails, myArticles)
    }
  }
  catch (e) {
    console.warn('Unable to handle article updates using event source', e)
  }
}

watch(() => settings.liveUpdates, (isEnabled) => {
  if (isEnabled) {
    initEventSource()
  }
  else {
    closeEventSource()
  }
})

watch(() => [route.params, route.query.t], gotoRouteSelection)

// prevent copying selected objects when copying model details properites
watch(() => articleDetailsOpen.value, (newValue) => {
  isArticleOrModelDetailsVisible.value = newValue
})

watch(() => whiteboardDetails.value, async (newDetails) => {
  if (newDetails) {
    // admin have full access
    if (newDetails.CreatedBy !== userStore.userProfile.Id || userStore.userProfile.AccountDetails.AccountTypeId === 1) {
      let isUserFound: boolean = false
      if (newDetails.SharedUsers.length) {
        const currentUser = newDetails.SharedUsers.find(user => user.UserId === userStore.userProfile.Id)
        if (currentUser) {
          isUserFound = true
          userPermission.value = currentUser.Write === 1
            ? whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType
            : currentUser.Read === 1
              ? whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType
              : currentUser.ReadAndComment === 1
                ? whiteboardConstants.userPermissions.readAndComment as WhiteboardUserPermissionType
                : whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType // default
        }
      }

      if (!isUserFound && newDetails.SharedUsersGroups.length) {
        for (const group of newDetails.SharedUsersGroups) {
          const groupPermission = group.Write === 1
            ? whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType
            : group.Read === 1
              ? whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType
              : group.ReadAndComment === 1
                ? whiteboardConstants.userPermissions.readAndComment as WhiteboardUserPermissionType
                : whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType // default
          if (groupPermission !== whiteboardConstants.userPermissions.write) {
            const groupDetails = await getAccessibleUserGroupDetails(group.GroupId)
            const users = groupDetails.data.Users
            if (users.length && users.some(user => user.UserId === userStore.userProfile.Id && user.Status)) {
              userPermission.value = groupPermission
              break
            }
          }
        }
      }
    }
    else {
      userPermission.value = whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType
    }

    if (userPermission.value !== whiteboardConstants.userPermissions.write) {
      isDisableCopyPasteUndoRedo.value = true
    }
  }
}, { immediate: true })

watch(() => route.params.objectId, (newObjectId) => {
  if (newObjectId && wb.value) {
    highlightObjectOnBoard(wb.value, newObjectId as string)
  }
})

function onObjectModified(e: fabric.IEvent<MouseEvent>) {
  if (summaryDialogRef.value && summaryDialogRef.value.visible && e.target
    && (e.target.type === whiteboardConstants.objectTypes.articleImage || e.target.type === whiteboardConstants.objectTypes.articleDetails)
  ) {
    summaryDialogRef.value.updateArticles(getWhiteboardArticles())
  }
}

onMounted(async () => {
  addEventListener('resize', onWindowResize)
  whiteboardConstants.abortController = new AbortController()
  if (userStore.activeCatalog) {
    await getWhiteboardDetails(userStore.activeCatalog.CatalogCode, Number(route.params.whiteboardId))
      .then((resp) => {
        whiteboardDetails.value = resp.data
      })
      .catch((error) => {
        console.error('Unable to get whiteboard details.', error)
        const message = 'Unable to open whiteboard, please try again later or contact support'
        let details = ''
        if (error.response && error.response.data && error.response.data.length) {
          details = error.response.data.map(x => x.ErrorMessage).join(', ')
        }
        notificationStore.addNotification({ message, details, type: 'Alert' })
        router.push({ name: 'Whiteboards' })
      })
  }
  if (whiteboardDetails.value && refCanvasContainer.value) {
    wb.value = new Whiteboard(whiteboardId.value, 'c', refCanvasContainer.value?.clientWidth, refCanvasContainer.value?.clientHeight, whiteboardDetails.value, route.query.moveToObject?.toString())

    wb.value.on('zoom', (e) => {
      zoom.value = Math.round(e.factor * 100)
    })

    wb.value.on('add-dynamic-grid', (e) => {
      objectPropertiesRef.value?.openDynamicPropertiesDialog(undefined, e.pos)
    })

    wb.value.canvas.on('mouse:dblclick', handleDoubleClick)
    wb.value.canvas.on('selection:created', onSelected)
    wb.value.canvas.on('selection:updated', onSelected)
    wb.value.canvas.on('object:added', onObjectModified)
    wb.value.canvas.on('object:modified', onObjectModified)
    wb.value.canvas.on('object:removed', onObjectModified)

    if (userStore.activeCatalog) {
      settings.showComments = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog?.CatalogCode || 0]?.showComments || true
      settings.liveUpdates = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog?.CatalogCode || 0]?.liveUpdates || true
      settings.includeDetails = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog?.CatalogCode || 0]?.includeDetails || true

      settings.showArticleDesignOptions = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]?.showArticleDesignOptions
      settings.showModelDesignOptions = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]?.showModelDesignOptions
    }

    wb.value.on('canvas-objects-loaded', (e: ICanvasObjectsLoadedEvent) => {
      const objectId = route.params.objectId
      if (objectId && wb.value) {
        highlightObjectOnBoard(wb.value, objectId as string)
      }
      const wbContent = e.wbDetails
      updateCanvasPermissions(wbContent)
    })
  }
  if (settings.liveUpdates) {
    initEventSource()
  }
  document.addEventListener('fullscreenchange', handleFullScreen, false)
  gotoRouteSelection()
})

onUnmounted(() => {
  removeEventListener('resize', onWindowResize)
  if (dbDoc.value) { dbDoc.value.destroy() }
  if (userDoc.value) { userDoc.value.destroy() }
  if (wb.value && wb.value.canvas) {
    // clear articleimage timeouts
    clearAllArticleTimeouts(wb.value.canvas)
    // wb.value.canvas.off()
    // wb.value.canvas.clear()
    wb.value.canvas.dispose() // This destroys all objects, events, and frees GPU resources
  }
  whiteboardConstants.abortController.abort('User Cancelled')
  whiteboardStore.setSettings(undefined)
  closeEventSource()
  document.removeEventListener('fullscreenchange', handleFullScreen)
})

function clearAllArticleTimeouts(canvas: fabric.Canvas) {
  canvas.getObjects().forEach((obj) => {
    if (obj instanceof WbArticleImage) {
      obj.cleanupNewArticleTimeouts()
    }
  })
}
</script>
